import React, { useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import { View, RefreshControl } from 'react-native';
import Alert from 'utils/packages/Alert';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import emoji from 'node-emoji';
import { noop, truncate } from 'lodash';
import { TouchableOpacity } from 'react-native-gesture-handler';
import Animated from 'react-native-reanimated';
import { FlashList } from '@shopify/flash-list';
import { AppView, CardView, SettingsItem, SettingsItemNavigate, Spacer, StatusBar, TextButtonLinkSeeAll, withAnimatedNavigationHeader, } from 'design-system';
import Button, { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import FlatListItemCard from 'design-system/FlatListItemCard';
import ListEmptyView from 'design-system/ListEmptyView';
import SectionHeader from 'design-system/SectionHeader';
import { hitSlop, rem } from 'design-system/values';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import { selectCurrentUserHasGroupSettlement, selectGroupAnalytics, selectGroupById, selectGroupTransactions, } from 'reducers/selectors';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import IconSend from 'design-system/icons/IconSend';
import { getGroupById } from 'actions';
import { IconSettings } from 'design-system/icons/IconSettings';
import useColors from 'hooks/useColors';
import { muteErrorAndResult, useFetchOld } from 'hooks';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMarginBottom } from 'hooks/useMarginBottom';
import IconCheck from 'design-system/icons/IconCheck';
import { IconCalculator } from 'design-system/icons';
import Modal from 'utils/packages/Modal';
import Text from 'design-system/Text';
import links from 'utils/links';
import ListLoader from 'design-system/ListLoader';
import { HeaderTitle } from 'design-system/hoc/withAnimatedNavigationHeader';
import { useNativeStackScreenOptions } from 'hooks/navigation/useStackNavigatorScreenOptions';
import useIsTablet from 'hooks/useIsTablet';
import { formatPlural } from 'utils';
import IconTransfer from 'design-system/icons/IconTransfer';
import { isWeb } from '../../constants';
import { ShareGroupModal } from './components/ShareGroupModal';
import { GroupExpenseCRow } from './components/GroupExpenseCRow';
import GroupTransaction from './components/GroupTransaction';
import GroupHeader from './components/GroupHeader';
import { AddTransactionModal } from './components/AddTransactionModal';
import { getUserIds } from './utils/getUserIds';
const GroupScreen = withAnimatedNavigationHeader(({ navigation, route, scrollHandler, scrollY }) => {
    const group = useAppSelector((store) => selectGroupById(store, route.params.id));
    const dispatch = useAppDispatch();
    const globalScreenOptions = useNativeStackScreenOptions();
    const isTablet = useIsTablet();
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTitle: () => (<HeaderTitle title={isTablet ? group?.name : truncate(group?.name, { length: 20 })} headerTitleStyle={globalScreenOptions.headerTitleStyle} scrollY={scrollY} hideTitleOnScroll/>),
        });
    }, [globalScreenOptions.headerTitleStyle, group?.name, isTablet, scrollY]);
    useEffect(() => {
        dispatch(getGroupById(route.params.id));
    }, [route.params.id]);
    if (!group)
        return null;
    return <GroupScreenContent group={group} scrollHandler={scrollHandler}/>;
}, ({ colors }) => ({
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
    hasCustomTitle: true,
}));
const AnimatedFlashList = Animated.createAnimatedComponent(FlashList);
const keyExtractor = (item) => 'id' in item ? item.id.toString() : item.title;
const getItemType = (item) => {
    if ('title' in item &&
        item.title !== 'noBalances' &&
        item.title !== 'settleButton') {
        return 'sectionHeader';
    }
    return 'row';
};
const GroupScreenContent = ({ group, scrollHandler }) => {
    const transactions = useAppSelector((store) => selectGroupTransactions(store, group.id));
    const analytics = useAppSelector((store) => selectGroupAnalytics(store, group.id));
    const hasGroupSettlement = useAppSelector((store) => selectCurrentUserHasGroupSettlement(store, group.id));
    const navigation = useMainStackNavigation();
    const [, , isRefreshing, fetch] = useFetchOld(muteErrorAndResult);
    const styles = useStyles(styleSet);
    const colors = useColors();
    const openGroupSettings = useCallback(() => {
        navigation.navigateDeprecated('GroupSettings', { groupId: group.id });
    }, [group.id]);
    const shareGroup = useCallback(() => {
        ShareGroupModal.show({
            onInviteContacts: () => navigation.push('GroupInvite', { group }),
            group,
        });
    }, [group]);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => (<View style={styles.headerButtonContainer}>
          <TouchableOpacity onPress={shareGroup} hitSlop={hitSlop}>
            <IconSend gradientColors={[
                    colors.elements.primary,
                    colors.elements.primary,
                ]}/>
          </TouchableOpacity>
          <Spacer w={16}/>
          <TouchableOpacity onPress={openGroupSettings} hitSlop={hitSlop}>
            <IconSettings color={colors.elements.primary}/>
          </TouchableOpacity>
        </View>),
        });
    }, [
        colors,
        group.name,
        openGroupSettings,
        shareGroup,
        styles.headerButtonContainer,
    ]);
    const onAddPress = useCallback(() => {
        if ((group.userIds.length ?? 0) < 2) {
            Alert.alert(emoji.emojify('Hold on :dizzy:'), 'Add at least one member to the group!', [{ text: 'OK' }]);
            return;
        }
        AddTransactionModal.show({ groupId: group.id, navigation });
    }, [group]);
    const handleTransactionPress = useCallback((transaction) => {
        navigation.navigateDeprecated('GroupTransaction', {
            transactionId: transaction.id,
            groupId: group.id,
        });
    }, [group.id]);
    const seeAllTransactions = useCallback(() => {
        navigation.navigateDeprecated('GroupTransactions', { groupId: group.id });
    }, [group.id]);
    const onSettleUp = useCallback(() => {
        if (!hasGroupSettlement) {
            Modal.showError(<Text TextThin-14 Secondary style={{ alignItems: 'center', justifyContent: 'center' }}>
          There is nothing to settle on your end; other group members might need
          to settle their outstanding debts. Do you want to know how settling is
          calculated?{' '}
          <Text Text-14 Primary onPress={links.openGroupSettleUp}>
            {`Learn\xa0more`}
          </Text>
        </Text>, Modal.hide, 'You have nothing to settle', 'Okay');
            return;
        }
        navigation.navigateDeprecated('GroupSettleUp', {
            groupId: group.id,
        });
    }, [group.id, hasGroupSettlement]);
    const filteredUsers = useMemo(() => analytics?.users?.filter((u) => !u.deleted), [analytics]);
    const sortedBalances = useMemo(() => filteredUsers
        ?.sort((a, b) => Math.abs(b.balance) - Math.abs(a.balance))
        .map((tb, index) => ({
        ...tb,
        itemIndex: index,
        itemsLength: filteredUsers?.length,
    })) ?? [], [filteredUsers]);
    const hasBalances = useMemo(() => sortedBalances.some((b) => 'balance' in b && b.balance !== 0), [sortedBalances]);
    const transactionsNodes = useMemo(() => transactions
        ?.slice(0, 3)
        .map((tr, index, array) => {
        const isFirst = index === 0;
        const isLast = index === array.length - 1;
        return {
            ...tr,
            isFirstInSection: isFirst,
            isLastInSection: isLast,
        };
    }) ?? [], [transactions]);
    const data = useMemo(() => [
        { title: 'Total balances' },
        ...(hasBalances
            ? [...sortedBalances, { title: 'settleButton' }]
            : [{ title: 'noBalances' }]),
        { title: 'Transactions' },
        ...transactionsNodes,
    ], [hasBalances, sortedBalances, transactionsNodes]);
    const isEmpty = !transactions?.length || group.userIds.length === 1;
    const isLoading = !transactions || !analytics;
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle);
    const usersByIds = useMemo(() => getUserIds(analytics?.users || []), [analytics]);
    const settlementsCount = useMemo(() => filteredUsers?.filter((u) => u.myBalance !== 0).length, [filteredUsers]);
    const renderItem = useCallback(({ item }) => {
        if ('title' in item) {
            if (item.title === 'noBalances') {
                return (<CardView noMarginTop>
              <SettingsItem title="Nothing to settle" icon={<IconCheck color={colors.elements.positive}/>}/>
            </CardView>);
            }
            if (item.title === 'settleButton') {
                return (<CardView webPadding>
              <SettingsItemNavigate titleFirst title="Settle up" description={settlementsCount ? (<View style={styles.settlement}>
                      <IconTransfer width={16} height={16} colorKey="brand"/>
                      <Text TextThin-14 Primary>
                        {settlementsCount} pending{' '}
                        {formatPlural('settlement', 'settlements', settlementsCount)}
                      </Text>
                    </View>) : null} icon={<IconCalculator colorKey="brand"/>} onPress={onSettleUp}/>
            </CardView>);
            }
            return (<SectionHeader style={styles.sectionHeader} button={item.title === 'Transactions' &&
                    (transactions?.length ?? 0) > 3 ? (<TextButtonLinkSeeAll onPress={seeAllTransactions}/>) : null}>
            {item.title}
          </SectionHeader>);
        }
        if ('handle' in item) {
            return (<FlatListItemCard index={item.itemIndex} style={styles.card} itemsLength={item.itemsLength}>
            <GroupExpenseCRow key={String(item.id)} groupUser={item} balance={item.balance} onPress={noop} currency={analytics?.currency}/>
          </FlatListItemCard>);
        }
        if ('amount' in item) {
            return (<GroupTransaction key={item.id} withWrapper transaction={item} usersByIds={usersByIds} onPress={handleTransactionPress}/>);
        }
        return null;
    }, [
        analytics?.currency,
        colors.elements.positive,
        handleTransactionPress,
        onSettleUp,
        seeAllTransactions,
        settlementsCount,
        styles.card,
        styles.sectionHeader,
        styles.settlement,
        transactions?.length,
        usersByIds,
    ]);
    const ListEmptyComponent = useMemo(() => {
        if (isLoading) {
            return <ListLoader smallMargin/>;
        }
        if (group.userIds.length === 1) {
            return (<ListEmptyView onCard compact title="You are the only one here!" subTitle="Add at least one member to start splitting your transactions" style={styles.empty}>
          <Button brandReversed small hug title="Add members" onPress={shareGroup}/>
        </ListEmptyView>);
        }
        if (!transactions.length) {
            return (<ListEmptyView onCard compact style={styles.empty} title="No activity yet" subTitle="Add your first transaction to the group!"/>);
        }
        return null;
    }, [
        group.userIds.length,
        isLoading,
        shareGroup,
        styles.empty,
        transactions?.length,
    ]);
    const ListHeaderComponent = useMemo(() => <GroupHeader group={group} onAddMembers={shareGroup}/>, [group, shareGroup]);
    const refreshControl = useMemo(() => (<RefreshControl onRefresh={() => fetch(getGroupById(group.id))} tintColor={colors.elements.primary} refreshing={isRefreshing}/>), [colors.elements.primary, group.id, isRefreshing]);
    return (<AppView withFrame={false}>
      <StatusBar barStyle="dark-content"/>
      <AnimatedFlashList extraData={styles.card} data={isEmpty || isLoading ? [] : data} keyExtractor={keyExtractor} onScroll={scrollHandler} scrollEventThrottle={16} renderItem={renderItem} ListHeaderComponent={ListHeaderComponent} ListEmptyComponent={ListEmptyComponent} getItemType={getItemType} refreshControl={refreshControl} contentContainerStyle={contentContainerStyle}/>
      <Button brand floating title="Add transaction" onPress={onAddPress}/>
    </AppView>);
};
const styleSet = createStyleSheets((colors) => ({
    sectionHeader: {
        marginHorizontal: rem(16),
        paddingTop: rem(8),
        paddingBottom: rem(16),
    },
    card: {
        backgroundColor: colors.cards.onDark,
    },
    headerButtonContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        // Same right padding as the other web header buttons
        ...(isWeb && { paddingRight: rem(16) }),
    },
    flex: { flex: 1 },
    empty: {
        flex: 0,
        // react-native-web reads flex: 0 as a 0% basis, which squashes the card
        ...(isWeb && { flexBasis: 'auto' }),
    },
    settlement: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: rem(6),
    },
}));
export default GroupScreen;
